import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProcessedTransactionList } from '../components/ProcessedTransactionList';
import { ProcessedTransactionFiltersComponent, ProcessedTransactionFilters, formatDateToISO } from '../components/ProcessedTransactionFilters';
import { processedTransactionApi } from '../lib/api/ProcessedTransactionApi';

const ProcessedTransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ProcessedTransactionFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<ProcessedTransactionFilters>({});
  const [isDownloading, setIsDownloading] = useState(false);

  const handleFiltersChange = (newFilters: ProcessedTransactionFilters) => {
    setFilters(newFilters);
  };

  /**
   * Format dates for ISO 8601 compatibility with the API
   * This converts the YYYY-MM-DD format from HTML date inputs to proper ISO 8601
   * format as required by the backend API. Start dates are set to beginning of day
   * (00:00:00 UTC) and end dates to end of day (23:59:59 UTC) to ensure complete
   * coverage of the selected date range.
   */
  const formattedFilters = useMemo(() => {
    const { startDate, endDate, ...rest } = filters;
    return {
      ...rest,
      startDate: formatDateToISO(startDate || ''),
      endDate: formatDateToISO(endDate || '', true), // true for end of day
    };
  }, [filters]);

  const handleApplyFilters = () => {
    setAppliedFilters(formattedFilters);
    toast.success('Filters applied successfully');
  };

  const handleClearFilters = () => {
    setFilters({});
    setAppliedFilters({});
    toast.success('Filters cleared');
  };

  const handleDownloadCSV = async () => {
    try {
      setIsDownloading(true);
      const csvData = await processedTransactionApi.downloadProcessedTransactionsCSV(
        undefined, // limit
        undefined, // offset
        appliedFilters.payerId,
        appliedFilters.paymentMethodId,
        appliedFilters.status === '' ? undefined : appliedFilters.status,
        appliedFilters.startDate,
        appliedFilters.endDate,
        undefined, // minAmount removed
        undefined  // maxAmount removed
      );

      // Create and download the CSV file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `processed-transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('CSV file downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCreateTransaction = () => {
    navigate('/processed-transactions/create');
  };

  const handleViewTransactionDetails = (transactionId: string) => {
    navigate(`/processed-transactions/${transactionId}`);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/">
          <Button variant="outline" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Transactions</h1>
        <p className="text-gray-600">
          Manage and monitor transactions across the platform.
        </p>
      </div>

      <ProcessedTransactionFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        onDownloadCSV={handleDownloadCSV}
        isLoading={isDownloading}
      />

      <ProcessedTransactionList
        onCreateTransaction={handleCreateTransaction}
        onViewTransactionDetails={handleViewTransactionDetails}
        filters={appliedFilters}
      />
    </div>
  );
};

export default ProcessedTransactionsPage;
